import { PaginatedDataDTO } from '@bottega52/commons-pagination';
import SeamlessImmutable from 'seamless-immutable';
import _ from 'lodash';
import { ICardInDTO } from '../../repository/jago/model/input/ICardInDTO';
import { ISubscriptionInDTO } from '../../repository/jago/model/input/ISubscriptionInDTO';
import { ICustomerInDTO } from './../../repository/jago/model/input/ICustomerInDTO';
import { IWalletCustomerDTO } from './../../repository/jago/model/input/IWalletCustomerDTO';
import { IWalletDetailedDTO } from './../../repository/jago/model/input/IWalletDetailedDTO';
import { CreditsActionsType } from './credits.actions';
import CreditsActionTypesEnum from './model/CreditsActionTypesEnum';
import IMarketplaceInDTO from '../../repository/jago/model/input/IMarketplaceInDTO';
import IClusterInDTO from '../../repository/jago/model/input/IClusterInDTO';
import { IPlantInDTO } from '../../repository/jago/model/input/IPlantInDTO';
import { IDomainInDTO } from '../../repository/jago/model/input/IDomainInDTO';
import { ICustomerContractInDTO } from '../../repository/jago/model/input/ICustomerContractInDTO';
import { ICustomerContractServiceInDTO } from '../../repository/jago/model/input/ICustomerContractServiceInDTO';
import { ICustomerServiceInDTO } from '../../repository/jago/model/input/ICustomerServiceInDTO';
import { ICustomerContractNotificationRuleInDTO } from '../../repository/jago/model/input/ICustomerContractNotificationRuleInDTO';
import { IDemoPlantInDTO } from '../../repository/jago/model/input/IDemoPlantInDTO';

export interface IMarketplaceInterface {
  data: PaginatedDataDTO<IMarketplaceInDTO> | {};
  selectedMarketplace?: IMarketplaceInDTO;
}

export interface IClusterInterface {
  data: PaginatedDataDTO<IClusterInDTO> | {};
}

export interface IMarketplaceSubscriptionsInterface {
  data: PaginatedDataDTO<ISubscriptionInDTO> | {};
  selectedMainTier?: ISubscriptionInDTO;
  selectedMainTierSubcriptions: PaginatedDataDTO<ISubscriptionInDTO> | {};
}

export interface ICreditsInterface {
  clusters: IClusterInterface,
  marketplaces: IMarketplaceInterface,
  customers: {
    data: PaginatedDataDTO<ICustomerInDTO> | {};
    selectedDetailedCustomer?: ICustomerInDTO;
  },
  wallets: {
    data: PaginatedDataDTO<IWalletCustomerDTO> | {};
    selectedWallet: IWalletDetailedDTO | {};
  },
  customerPlants: {
    data: PaginatedDataDTO<IDomainInDTO> | {};
    selectedCustomerPlant: IDomainInDTO | {};
  },
  demoPlants: {
    data: PaginatedDataDTO<IDemoPlantInDTO> | {};
    selectedDemoPlant: IDemoPlantInDTO | {};
  },
  customerContracts: {
    data: PaginatedDataDTO<ICustomerContractInDTO> | {};
    selectedCustomerContract: ICustomerContractInDTO | {};
  },
  customerContractServices: {
    data: PaginatedDataDTO<ICustomerContractServiceInDTO> | {};
    customerAllContractServices: {
      data: ICustomerContractServiceInDTO[] | {};
    },
    selectedCustomerContractService: ICustomerContractServiceInDTO | {};
  },
  allCustomerContracts: {
    data: ICustomerContractInDTO[] | {};
  },
  allCustomerContractServices: {
    data: ICustomerContractServiceInDTO[] | {};
  },
  allCustomers: {
    data: ICustomerInDTO[] | {};
  },
  customerContractNotificationRules: {
    data: PaginatedDataDTO<ICustomerContractNotificationRuleInDTO> | {};
    selectedCustomerContractNotificationRule: ICustomerContractNotificationRuleInDTO | {};
  },
  marketplaceSubscriptions: IMarketplaceSubscriptionsInterface,
  systemCards: {
    data: PaginatedDataDTO<ICardInDTO> | {};
    wallets: {
      data: PaginatedDataDTO<IWalletCustomerDTO> | {};
    },
    customersSearch: {
      data: PaginatedDataDTO<ICustomerInDTO> | {};
    },
    plants: {
      data: PaginatedDataDTO<IPlantInDTO> | {};
    },
    selectedCustomer: ICustomerInDTO | {};
  },
  domains: {
    data: PaginatedDataDTO<IDomainInDTO> | {};
  },
  periodicDomains: {
    data: PaginatedDataDTO<IDomainInDTO> | {};
  },
  staticServices: ICustomerServiceInDTO[];
}

const initialState = SeamlessImmutable<ICreditsInterface>({
  clusters: {
    data: {},
  },
  marketplaces: {
    data: {},
  },
  customers: {
    data: {},
    selectedDetailedCustomer: undefined,
  },
  wallets: {
    data: {},
    selectedWallet: {},
  },
  customerPlants: {
    data: {},
    selectedCustomerPlant: {},
  },
  demoPlants: {
    data: {},
    selectedDemoPlant: {},
  },
  customerContracts: {
    data: {},
    selectedCustomerContract: {},
  },
  customerContractServices: {
    data: {},
    customerAllContractServices: {
      data: {},
    },
    selectedCustomerContractService: {},
  },
  allCustomerContracts: {
    data: {},
  },
  allCustomerContractServices: {
    data: {},
  },
  allCustomers: {
    data: {},
  },
  customerContractNotificationRules: {
    data: {},
    selectedCustomerContractNotificationRule: {},
  },
  marketplaceSubscriptions: {
    data: {},
    selectedMainTierSubcriptions: {},
  },
  systemCards: {
    data: {},
    wallets: {
      data: {}
    },
    customersSearch: {
      data: {}
    },
    plants: {
      data: {}
    },
    selectedCustomer: {}
  },
  domains: {
    data: {}
  },
  periodicDomains: {
    data: {}
  },
  staticServices: [],
});

// eslint-disable-next-line import/no-anonymous-default-export
export default function(state: SeamlessImmutable.ImmutableObject<ICreditsInterface> = initialState, action: CreditsActionsType) {
  switch (action.type) {
    case CreditsActionTypesEnum.SAVE_CLUSTERS:
      return state.setIn(['clusters', 'data'], action.payload);
    case CreditsActionTypesEnum.SAVE_MARKETPLACES:
      return state.setIn(['marketplaces', 'data'], action.payload);
    case CreditsActionTypesEnum.SET_SELECTED_MARKETPLACE:
      return state.setIn(['marketplaces', 'selectedMarketplace'], action.payload);
    case CreditsActionTypesEnum.SAVE_CUSTOMERS:
      return state.setIn(['customers', 'data'], action.payload);
    case CreditsActionTypesEnum.SET_SELECTED_DETAILED_CUSTOMER:
      return state.setIn(['customers', 'selectedDetailedCustomer'], action.payload);
    case CreditsActionTypesEnum.SAVE_WALLETS:
      return state.setIn(['wallets', 'data'], action.payload);
    case CreditsActionTypesEnum.APPEND_WALLET: {
      const wallets = state.wallets.data.asMutable() as PaginatedDataDTO<IWalletCustomerDTO>;
      if (!wallets || !wallets.content) {
        return state;
      }
      return state.setIn(['wallets', 'data', 'content'], [...wallets.content, action.payload]);
    }
    case CreditsActionTypesEnum.UPDATE_WALLET: {
      const wallets = state.wallets.data.asMutable() as PaginatedDataDTO<IWalletCustomerDTO>;
      if (wallets && wallets.content) {
        const walletToUpdateIndex = _.findIndex(wallets.content, { id: action.payload.id });
        if (walletToUpdateIndex > -1) {
          return state.setIn(['wallets', 'data', 'content', walletToUpdateIndex as unknown as string], action.payload);
        }
      }
      return state;
    }
    case CreditsActionTypesEnum.SET_SELECTED_WALLET:
      return state.setIn(['wallets', 'selectedWallet'], action.payload);
    case CreditsActionTypesEnum.SAVE_MARKETPLACE_SUBSCRIPTIONS:
      return state.setIn(['marketplaceSubscriptions', 'data'], action.payload);
    case CreditsActionTypesEnum.SET_SELECTED_MARKETPLACE_MAIN_TIER:
      return state.setIn(['marketplaceSubscriptions', 'selectedMainTier'], action.payload);
    case CreditsActionTypesEnum.SAVE_SELECTED_MARKETPLACE_MAIN_TIER_SUBSCRIPTIONS:
      return state.setIn(['marketplaceSubscriptions', 'selectedMainTierSubcriptions'], action.payload);
    case CreditsActionTypesEnum.SAVE_SYSTEM_CARDS:
      return state.setIn(['systemCards', 'data'], action.payload);
    case CreditsActionTypesEnum.SAVE_WALLETS_ALL:
      return state.setIn(['systemCards', 'wallets', 'data'], action.payload);
    case CreditsActionTypesEnum.SAVE_CUSTOMERS_SEARCH:
      return state.setIn(['systemCards', 'customersSearch', 'data'], action.payload);
    case CreditsActionTypesEnum.SAVE_CUSTOMER_PLANTS:
      return state.setIn(['systemCards', 'plants', 'data'], action.payload);
    case CreditsActionTypesEnum.SAVE_ALL_DOMAINS:
      return state.setIn(['domains', 'data'], action.payload);
    case CreditsActionTypesEnum.SAVE_PERIODIC_DOMAINS:
      return state.setIn(['periodicDomains', 'data'], action.payload);
    case CreditsActionTypesEnum.SAVE_CUSTOMER_CONTRACTS:
      return state.setIn(['customerContracts', 'data'], action.payload);
    case CreditsActionTypesEnum.SET_SELECTED_CUSTOMER_CONTRACT:
      return state.setIn(['customerContracts', 'selectedCustomerContract'], action.payload);
    case CreditsActionTypesEnum.SAVE_CUSTOMER_CONTRACT_SERVICES:
      return state.setIn(['customerContractServices', 'data'], action.payload);
    case CreditsActionTypesEnum.SAVE_CUSTOMER_ALL_CONTRACT_SERVICES:
      return state.setIn(['customerContractServices', 'customerAllContractServices', 'data'], action.payload);
    case CreditsActionTypesEnum.SET_SELECTED_CUSTOMER_CONTRACT_SERVICE:
      return state.setIn(['customerContractServices', 'selectedCustomerContractService'], action.payload);
    case CreditsActionTypesEnum.SET_SELECTED_CUSTOMER_PLANT:
      return state.setIn(['customerPlants', 'selectedCustomerPlant'], action.payload);
    case CreditsActionTypesEnum.LOAD_STATIC_SERVICES:
      return state.set('staticServices', action.payload);
    case CreditsActionTypesEnum.SAVE_ALL_CUSTOMER_CONTRACTS:
      return state.setIn(['allCustomerContracts', 'data'], action.payload);
    case CreditsActionTypesEnum.SAVE_ALL_CUSTOMER_CONTRACT_SERVICES:
      return state.setIn(['allCustomerContractServices', 'data'], action.payload);
    case CreditsActionTypesEnum.SAVE_ALL_CUSTOMERS:
      return state.setIn(['allCustomers', 'data'], action.payload);
    case CreditsActionTypesEnum.SAVE_CUSTOMER_CONTRACT_NOTIFICATION_RULES:
      return state.setIn(['customerContractNotificationRules', 'data'], action.payload);
    case CreditsActionTypesEnum.SET_SELECTED_CUSTOMER_CONTRACT_NOTIFICATION_RULE:
      return state.setIn(['customerContractNotificationRules', 'selectedCustomerContractNotificationRule'], action.payload);
    case CreditsActionTypesEnum.SAVE_DEMO_PLANTS:
      return state.setIn(['demoPlants', 'data'], action.payload);
    case CreditsActionTypesEnum.SET_SELECTED_DEMO_PLANT:
      return state.setIn(['demoPlants', 'selectedDemoPlant'], action.payload);
    default:
      return state;
  }
}
